if (!customElements.get("variant-radios")) {
  class VariantRadios extends HTMLElement {
    constructor() {
      super();
      this.hasRunInitialCombinedSync = false;
      this.sectionHtmlCache = new Map();
      this.activeFetchController = null;
      this.activeRenderToken = 0;
      this.boundOnVariantChange = null;
      this.hasBoundChangeListener = false;
    }

    connectedCallback() {
      this.updateOptions();
      this.updateMasterId();
      this.addListeners();
      this.syncCombinedListingOnLoad();
    }

    /**
     * Adds event listeners.
     */
    addListeners() {
      if (this.hasBoundChangeListener) return;

      this.boundOnVariantChange = this.onVariantChange.bind(this);
      this.addEventListener("change", this.boundOnVariantChange);
      this.hasBoundChangeListener = true;
    }

    /**
     * Handles variant change events.
     * @param {Event} event
     */
    onVariantChange(event) {
      this.updateOptions();
      this.updateMasterId();
      this.removeErrorMessage();

      const selectedOption = this.getSelectedOptionFromEvent(event);
      const linkedProductUrl = this.getLinkedProductUrl(selectedOption);
      const linkedVariantId = this.getLinkedVariantId();
      const selectedOptionVariantId =
        this.getSelectedOptionVariantId(selectedOption);
      const optionValueIds = this.getSelectedOptionValueIds();
      const isLinkedProductSwitch =
        linkedProductUrl && linkedProductUrl !== this.productUrl;

      // Quick-add swaps linked products at the modal level.
      // Keep local variant state in sync and avoid section-render updates here.
      if (this.closest(".quick-add")) {
        if (isLinkedProductSwitch) return;

        if (!this.currentVariant) {
          this.setUnavailable();
          return;
        }

        const nextVariantId =
          selectedOptionVariantId ?? linkedVariantId ?? this.currentVariant.id;
        this.updateVariantInputs({ variantId: nextVariantId });
        this.toggleAddButton(
          !this.currentVariant.available,
          this.currentVariant.available
            ? this.getAvailableButtonText()
            : "soldOut",
        );
        return;
      }

      // Preserve non-linked option value (e.g. size) across product switches.
      if (isLinkedProductSwitch) {
        this.pendingNonLinkedOptionValue =
          this.getSelectedNonLinkedOptionValue();
      }

      if (!this.currentVariant) {
        // Combined listings can point to a linked product even when the current
        // option state does not resolve to a purchasable variant.
        const requestedVariantId =
          selectedOptionVariantId ?? linkedVariantId ?? null;

        this.updateURL(linkedProductUrl, requestedVariantId);
        this.renderProductInfo(
          linkedProductUrl,
          requestedVariantId,
          optionValueIds,
        );
        this.updateVariantInputs({ variantId: linkedVariantId });

        // If we are staying on the same product and no variant can be resolved,
        // the selection is genuinely unavailable.
        if (!isLinkedProductSwitch) {
          this.setUnavailable();
        }

        return;
      }

      // Keep button disabled to avoid text flickering
      const alreadyDisabled =
        this.addToCartButtons &&
        [...this.addToCartButtons].some((b) => b.hasAttribute("disabled"));
      if (!alreadyDisabled) {
        this.toggleAddButton(true, "addToCart");
      }

      // During linked product switches, prefer the linked URL variant so we
      // never carry an old product's variant ID onto the child product URL.
      const nextVariantId = isLinkedProductSwitch
        ? (selectedOptionVariantId ?? linkedVariantId ?? this.currentVariant.id)
        : (selectedOptionVariantId ?? this.currentVariant.id);

      this.updateURL(linkedProductUrl, nextVariantId);
      this.updateVariantInputs({ variantId: nextVariantId });
      this.renderProductInfo(linkedProductUrl, nextVariantId, optionValueIds);
    }

    /**
     * Ensures combined-listing child product content is rendered on first load
     * when the selected option already points to a linked child product.
     */
    syncCombinedListingOnLoad() {
      if (this.hasRunInitialCombinedSync) return;
      this.hasRunInitialCombinedSync = true;

      if (this.closest(".quick-add")) return;
      if (!this.currentVariant) return;

      const linkedProductUrl = this.getLinkedProductUrl();
      const linkedVariantId = this.getLinkedVariantId();
      const nextVariantId = linkedVariantId ?? this.currentVariant.id;
      if (!linkedProductUrl || linkedProductUrl === this.productUrl) return;

      const url = `${linkedProductUrl}?variant=${nextVariantId}`;
      window.history.replaceState({}, "", url);

      // Avoid a full section re-render on first paint to prevent CLS,
      // especially on desktop combined-listing media grids.
      this.updateVariantInputs({
        dispatchChange: false,
        variantId: nextVariantId,
      });
    }

    /**
     * Sets the options based on selected inputs.
     */
    updateOptions() {
      this.options = Array.from(this.variantFieldsets).map((fieldset) => {
        const input = fieldset.querySelector("[data-input]:checked");
        const selectedOption = fieldset.querySelector("[data-selected-option]");

        if (selectedOption) {
          selectedOption.textContent = input
            ? input.value
            : fieldset.dataset.defaultOption;
        }

        return input ? input.value : null;
      });
    }

    /**
     * Updates the current variant based on selected options.
     */
    updateMasterId() {
      this.currentVariant = this.variantData.find((variant) => {
        return !variant.options
          .map((option, index) => this.options[index] === option)
          .includes(false);
      });
    }

    /**
     * Updates the URL with the current variant ID.
     */
    updateURL(nextProductUrl = this.productUrl, requestedVariantId = null) {
      if (this.closest(".quick-add")) return;

      const variantId = requestedVariantId ?? this.currentVariant?.id;
      const url = variantId
        ? `${nextProductUrl}?variant=${variantId}`
        : `${nextProductUrl}`;

      window.history.replaceState({}, "", url);
    }

    /**
     * Updates the hidden variant input.
     */
    updateVariantInputs({ dispatchChange = true, variantId = null } = {}) {
      const nextVariantId = variantId ?? this.currentVariant?.id;
      if (!nextVariantId) return;

      const nextVariantIdString = String(nextVariantId);

      this.variantInputs.forEach((variantInput) => {
        variantInput.setAttribute("data-variant-id", nextVariantIdString);
        variantInput.dataset.variantId = nextVariantIdString;
        if (dispatchChange) {
          variantInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });

      const quickAddRoot = this.closest(".quick-add");
      const wishlistButton = quickAddRoot?.querySelector("[data-add-to-wishlist]");
      if (wishlistButton) {
        wishlistButton.setAttribute("data-variant-id", nextVariantIdString);
        wishlistButton.dataset.variantId = nextVariantIdString;
      }
    }

    /**
     * Sets the state when no variant is found.
     */
    removeErrorMessage() {
      if (!this.errorMessage) return;

      this.errorMessage.textContent = "";
      this.errorMessage.toggleAttribute("hidden", true);
    }

    /**
     * Handles the error message display.
     * @param errorMessage
     */
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors || !this.errorMessage) return;

      this.errorMessage.toggleAttribute("hidden", !errorMessage);

      if (errorMessage) this.errorMessage.textContent = errorMessage;
    }

    /**
     * Sets the state when no variant is found.
     * @param {Event} event
     */
    renderProductInfo(
      nextProductUrl = this.productUrl,
      requestedVariantId = null,
      optionValueIds = [],
    ) {
      const renderToken = ++this.activeRenderToken;
      const sectionId = this.sectionId;
      const productUrl = this.productUrl;
      const variantId = requestedVariantId ?? this.currentVariant?.id;
      const newProductUrl = nextProductUrl || productUrl;
      const optionValuesParam = optionValueIds.length
        ? `option_values=${optionValueIds.join(",")}`
        : "";
      // Match Horizon behavior: when option_values are present, let Shopify
      // resolve the variant from option selections instead of forcing a
      // specific variant id in the request.
      const variantParam =
        !optionValuesParam && variantId ? `variant=${variantId}` : "";
      const queryParams = [
        `section_id=${sectionId}`,
        optionValuesParam,
        variantParam,
      ].filter(Boolean);
      const url = `${newProductUrl}?${queryParams.join("&")}`;

      // Reuse cached section HTML for snappier repeat option switches.
      const cachedHtml = this.sectionHtmlCache.get(url);
      if (cachedHtml) {
        this.applyRenderedProductInfo(
          cachedHtml,
          productUrl,
          newProductUrl,
          renderToken,
        );
        return;
      }

      // Cancel any stale request so only the latest selection wins.
      if (this.activeFetchController) {
        this.activeFetchController.abort();
      }

      this.activeFetchController = new AbortController();

      fetch(url, { signal: this.activeFetchController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.sectionHtmlCache.set(url, responseText);
          this.applyRenderedProductInfo(
            responseText,
            productUrl,
            newProductUrl,
            renderToken,
          );
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("Variant section render failed", error);
          }
        })
        .finally(() => {
          if (renderToken === this.activeRenderToken) {
            this.activeFetchController = null;
          }
        });
    }

    /**
     * Applies section-rendered HTML to the current page.
     * @param {string} responseText
     * @param {string} productUrl
     * @param {string} newProductUrl
     */
    applyRenderedProductInfo(
      responseText,
      productUrl,
      newProductUrl,
      renderToken = this.activeRenderToken,
    ) {
      if (renderToken !== this.activeRenderToken) {
        return;
      }

      const html = new DOMParser().parseFromString(responseText, "text/html");

      if (productUrl !== newProductUrl) {
        this.dataset.productUrl = newProductUrl;
        this.updateSectionContent(html, true);

        // After product switch, re-apply preserved non-linked option (size)
        // by value and re-render once with the new product's option_value ids.
        if (
          this.pendingNonLinkedOptionValue &&
          !this.isReapplyingNonLinkedOption
        ) {
          const applied = this.applyNonLinkedOptionValue(
            this.pendingNonLinkedOptionValue,
          );
          this.pendingNonLinkedOptionValue = null;

          if (applied) {
            this.isReapplyingNonLinkedOption = true;
            const updatedOptionValueIds = this.getSelectedOptionValueIds();
            this.renderProductInfo(
              this.productUrl,
              null,
              updatedOptionValueIds,
            );
            return;
          }
        }
      } else {
        this.updateSectionContent(html);
      }

      const renderedVariantId = this.updateButton(html);

      // Trust the server-rendered variant as source-of-truth for URL/input sync.
      if (renderedVariantId) {
        this.updateVariantInputs({
          dispatchChange: false,
          variantId: renderedVariantId,
        });
        this.updateURL(newProductUrl, renderedVariantId);
      }

      this.updateVariantInfo(html);
      this.updateStockAndDelivery(html);

      if (this.isReapplyingNonLinkedOption) {
        this.isReapplyingNonLinkedOption = false;
      }
    }

    /**
     * Returns the selected option element from a change event.
     * @param {Event} event
     * @returns {HTMLElement|null}
     */
    getSelectedOptionFromEvent(event) {
      if (!event || !(event.target instanceof HTMLElement)) return null;

      if (event.target instanceof HTMLSelectElement) {
        return event.target.options[event.target.selectedIndex] ?? null;
      }

      return event.target;
    }

    /**
     * Returns selected option-value IDs used by Shopify combined-listing API.
     * @returns {string[]}
     */
    getSelectedOptionValueIds() {
      return Array.from(this.querySelectorAll("[data-input]:checked"))
        .map((input) => input.dataset.optionValueId)
        .filter(Boolean);
    }

    /**
     * Returns the variant ID attached to the selected option when available.
     * @param {HTMLElement|null} selectedOption
     * @returns {number|null}
     */
    getSelectedOptionVariantId(selectedOption) {
      const variantParam = selectedOption?.dataset?.optionVariantId;
      const variantId = variantParam ? Number(variantParam) : NaN;

      return Number.isFinite(variantId) ? variantId : null;
    }

    /**
     * Returns currently selected value of the non-linked option fieldset
     * (e.g. size), which does not carry connected product URLs.
     * @returns {string|null}
     */
    getSelectedNonLinkedOptionValue() {
      const nonLinkedFieldset = Array.from(this.variantFieldsets).find(
        (fieldset) =>
          !fieldset.querySelector("[data-input][data-connected-product-url]"),
      );
      if (!nonLinkedFieldset) return null;

      const checkedInput = nonLinkedFieldset.querySelector(
        "[data-input]:checked",
      );
      return checkedInput ? checkedInput.value : null;
    }

    /**
     * Applies a non-linked option value (size) by value match on current picker.
     * Does not dispatch events; caller can decide if a re-render is needed.
     * @param {string} value
     * @returns {boolean}
     */
    applyNonLinkedOptionValue(value) {
      const nonLinkedFieldset = Array.from(this.variantFieldsets).find(
        (fieldset) =>
          !fieldset.querySelector("[data-input][data-connected-product-url]"),
      );
      if (!nonLinkedFieldset) return false;

      const matchingInput = Array.from(
        nonLinkedFieldset.querySelectorAll("[data-input]"),
      ).find((input) => input.value === value);

      if (!matchingInput) return false;

      const isDisabled = matchingInput
        .closest("[data-variant-option]")
        ?.classList.contains("radio--disabled");

      if (isDisabled || matchingInput.checked) return false;

      matchingInput.checked = true;
      this.updateOptions();
      this.updateMasterId();
      return true;
    }

    /**
     * Returns the linked child product URL from selected options when available.
     * Falls back to the current product URL.
     * @returns {string}
     */
    getLinkedProductUrl(selectedOption = null) {
      const selectedInput =
        selectedOption ||
        Array.from(
          this.querySelectorAll("[data-input]:checked[data-product-url]"),
        ).find((input) => input.dataset.productUrl);
      const selectedUrl =
        selectedInput?.dataset?.connectedProductUrl ||
        selectedInput?.dataset?.productUrl;

      if (!selectedUrl) return this.productUrl;

      try {
        const parsed = new URL(selectedUrl, window.location.origin);
        return parsed.pathname;
      } catch (error) {
        return selectedUrl.split("?")[0].split("#")[0] || this.productUrl;
      }
    }

    /**
     * Returns the linked child variant ID from selected option URL when available.
     * @returns {number|null}
     */
    getLinkedVariantId() {
      const selectedInput = Array.from(
        this.querySelectorAll("[data-input]:checked[data-product-url]"),
      ).find((input) => input.dataset.productUrl);
      const selectedUrl = selectedInput?.dataset.productUrl;

      if (!selectedUrl) return null;

      try {
        const parsed = new URL(selectedUrl, window.location.origin);
        const variantParam = parsed.searchParams.get("variant");
        const variantId = variantParam ? Number(variantParam) : NaN;

        return Number.isFinite(variantId) ? variantId : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * Sets the state when no variant is found.
     */
    updateSectionContent(html, variants = false) {
      this.updateContent(html, "price", true);
      this.updateContent(html, "featured-media", true);
      this.updateContent(html, "option", true);
      this.updateContent(html, "badges", true);

      if (variants) {
        this.updateContent(html, "variants");
        this.updateContent(html, "buy-buttons");
      }
    }

    /**
     * Updates specific content based on selector.
     * @param {Document} html
     * @param {string} selector
     * @param {boolean} outer
     */
    updateContent(html, selector, outer = false) {
      const oldElements = Array.from(
        document.querySelectorAll(`[data-variant-${selector}]`),
      ).filter((el) => !el.closest("product-card"));
      const newElements = Array.from(
        html.querySelectorAll(`[data-variant-${selector}]`),
      ).filter((el) => !el.closest("product-card"));
      const method = outer ? "outerHTML" : "innerHTML";

      oldElements.forEach((oldElement, index) => {
        const newElement = newElements[index];

        if (newElement) oldElement[method] = newElement[method];
      });
    }

    updateStockAndDelivery(html) {
      const currentDeliveryEls = Array.from(
        document.querySelectorAll("[data-delivery-countdown]"),
      );
      if (!currentDeliveryEls.length) return;

      const nextDeliveryEls = Array.from(
        html.querySelectorAll("[data-delivery-countdown]"),
      );
      if (!nextDeliveryEls.length) return;

      currentDeliveryEls.forEach((currentDeliveryEl, index) => {
        const nextDeliveryEl = nextDeliveryEls[index] || nextDeliveryEls[0];
        currentDeliveryEl.outerHTML = nextDeliveryEl.outerHTML;
      });
    }

    /**
     * Updates the add to cart button state.
     * @param {Document} html
     */
    updateButton(html) {
      const addButtonUpdated = html.querySelector(
        "[data-add-to-cart], [data-add-to-wishlist]",
      );
      const isDisabled = addButtonUpdated
        ? addButtonUpdated.hasAttribute("disabled")
        : true;
      const buttonText = isDisabled
        ? "soldOut"
        : this.getAvailableButtonText();

      const renderedVariantParam = addButtonUpdated?.dataset?.variantId;
      const renderedVariantId = renderedVariantParam
        ? Number(renderedVariantParam)
        : NaN;

      this.toggleAddButton(isDisabled, buttonText);

      return Number.isFinite(renderedVariantId) ? renderedVariantId : null;
    }

    /**
     * Sets the state when no variant is found.
     * @param {boolean} disable
     * @param {string} buttonText
     */
    toggleAddButton(disable = true, buttonText = "addToCart") {
      if (!this.addToCartButtons) return;

      const variantStrings = window.variantStrings || {
        addToCart: "Add to cart",
        addToWishlist: "Add to wishlist",
        soldOut: "Sold out",
        unavailable: "Unavailable",
      };

      this.addToCartButtons.forEach((button, index) => {
        button.toggleAttribute("disabled", disable);
        if (this.addToCartButtonTexts[index]) {
          this.addToCartButtonTexts[index].textContent =
            variantStrings[buttonText] || variantStrings.addToCart;
        }
      });
    }

    getAvailableButtonText() {
      if (this.isWishlistQuickAdd) return "addToWishlist";
      const buttons = this.addToCartButtons;
      if (buttons.length > 0 && buttons[0].closest("[data-preorder]")) return "preOrder";
      return "addToCart";
    }

    get isWishlistQuickAdd() {
      return this.closest(".quick-add")?.dataset.quickAddMode === "wishlist";
    }

    /**
     * Updates content when a variant is unavailable.
     */
    setUnavailable() {
      this.toggleAddButton(true, "unavailable");
    }

    updateVariantInfo(html) {
      if (!this.variantInfoElement) return;

      const variantInfoEl = html.querySelector("[data-variant-info]");

      if (variantInfoEl) {
        this.variantInfoElement.innerHTML = variantInfoEl.innerHTML;
      }
    }

    /**
     * Returns variant data from JSON script.
     * @returns {Array}
     */
    get variantData() {
      const variantDataScript = this.querySelector('[type="application/json"]');
      return JSON.parse(variantDataScript.textContent);
    }

    /**
     * Returns all variant fieldsets.
     * @returns {NodeListOf<Element>}
     */
    get variantFieldsets() {
      return this.querySelectorAll("[data-variant-fieldset]");
    }

    /**
     * Returns the hidden variant input.
     * @returns {NodeListOf<HTMLInputElement>}
     */
    get variantInputs() {
      const quickAddRoot = this.closest(".quick-add");
      if (quickAddRoot) {
        return quickAddRoot.querySelectorAll("[data-variant-id]");
      }

      return Array.from(document.querySelectorAll("[data-variant-id]")).filter(
        (el) => !el.closest("product-card"),
      );
    }

    /**
     * Returns the product URL.
     * @returns {string}
     */
    get productUrl() {
      return this.dataset.productUrl;
    }

    /**
     * Returns the section ID.
     * @returns {string}
     */
    get sectionId() {
      return this.dataset.section;
    }

    /**
     * Returns the add to cart button.
     * @returns {NodeListOf<HTMLButtonElement>}
     */
    get addToCartButtons() {
      const quickAddRoot = this.closest(".quick-add");
      if (quickAddRoot) {
        return quickAddRoot.querySelectorAll(
          "[data-add-to-cart], [data-add-to-wishlist]",
        );
      }

      return document.querySelectorAll("[data-add-to-cart]");
    }

    /**
     * Returns the add to cart button text.
     * @returns {NodeListOf<HTMLElement>}
     */
    get addToCartButtonTexts() {
      return Array.from(this.addToCartButtons).map((button) =>
        button.querySelector(
          "[data-add-to-cart-text], [data-add-to-wishlist-text]",
        ),
      );
    }

    get variantInfoElement() {
      const quickAddRoot = this.closest(".quick-add");
      if (quickAddRoot) {
        return quickAddRoot.querySelector("[data-variant-info]");
      }

      return document.querySelector("[data-variant-info]");
    }

    /**
     * Returns the error message element.
     * @returns {HTMLElement}
     */
    get errorMessage() {
      return this.querySelector("[data-error-message]");
    }
  }

  customElements.define("variant-radios", VariantRadios);
}